import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  getCurrentUser,
  getMfaStatus,
  login as loginApi,
  logout as logoutApi,
  refreshToken,
  verifyMfa as verifyMfaApi,
} from "#/features/auth/api";
import type {
  AuthTokenResponse,
  CurrentUserResponse,
  LoginRequest,
  LoginResponse,
  MfaVerifyRequest,
} from "#/features/auth/types";
import {
  clearAuthStorage,
  getStoredExpiresAt,
  getStoredToken,
  setAuthSession,
} from "#/lib/auth-storage";
import {
  ACTIVITY_EVENTS,
  INACTIVITY_LIMIT_MS,
  REFRESH_BEFORE_EXPIRY_MS,
} from "#/lib/session-policy";

type AuthContextType = {
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  token: string | null;
  currentUser: CurrentUserResponse | null;
  /**
   * Whether the current user has MFA enrolled.
   * null = not yet resolved (bootstrap in progress).
   * false = enrolled but MFA disabled → must set up before accessing the app.
   */
  mfaEnabled: boolean | null;
  /** Manually update MFA state (used by MfaSettingsPanel after enable/disable). */
  setMfaEnabled: (enabled: boolean) => void;
  /** ISO timestamp of when the current JWT expires; null when unauthenticated. */
  sessionExpiresAt: string | null;
  /** Submit credentials. Returns MfaRequiredResponse if MFA is needed, or AuthTokenResponse on success. */
  login: (payload: LoginRequest) => Promise<LoginResponse>;
  /** Complete MFA verification after a login challenge. Sets session and fetches current user. */
  verifyMfa: (payload: MfaVerifyRequest) => Promise<AuthTokenResponse>;
  signOut: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [currentUser, setCurrentUser] = useState<CurrentUserResponse | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  /** null = unknown, true/false = resolved */
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(
    () => getStoredExpiresAt(),
  );

  const isAuthenticated = Boolean(token && currentUser);

  const refreshCurrentUser = useCallback(async () => {
    const me = await getCurrentUser();
    setCurrentUser(me);
  }, []);

  // ── Bootstrap: restore session from localStorage ──────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const storedToken = getStoredToken();

      if (!storedToken) {
        if (!cancelled) {
          setToken(null);
          setCurrentUser(null);
          setMfaEnabled(null);
          setIsBootstrapping(false);
        }
        return;
      }

      try {
        // Fetch user and MFA status in parallel for fastest bootstrap.
        const [me, mfaStatus] = await Promise.all([
          getCurrentUser(),
          getMfaStatus().catch(() => null), // non-fatal; default to gated
        ]);

        if (!cancelled) {
          setToken(storedToken);
          setCurrentUser(me);
          setMfaEnabled(mfaStatus?.enabled ?? false);
          setSessionExpiresAt(getStoredExpiresAt());
        }
      } catch {
        clearAuthStorage();
        if (!cancelled) {
          setToken(null);
          setCurrentUser(null);
          setMfaEnabled(null);
        }
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    }

    void bootstrap();
    return () => { cancelled = true; };
  }, []);

  // ── Sliding session: silently refresh the JWT before it hard-expires, as
  // long as the user has been active recently. Uses the same activity
  // definition and idle threshold as InactivityLogout's own idle timer (see
  // lib/session-policy.ts) so the two mechanisms can't disagree about
  // whether the user is "active." The refresh check is scheduled relative
  // to the token's actual expiry (REFRESH_BEFORE_EXPIRY_MS before it lapses)
  // rather than a flat poll interval, so there's no blind-spot window where
  // resumed activity goes unnoticed until the hard JWT deadline has already
  // passed.
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!token) return; // don't run when logged out

    const bump = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, bump, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, bump));
    };
  }, [token]);

  useEffect(() => {
    if (!token || !sessionExpiresAt) return;

    const expiresMs = new Date(sessionExpiresAt).getTime();
    const delay = Math.max(expiresMs - REFRESH_BEFORE_EXPIRY_MS - Date.now(), 0);
    let cancelled = false;

    async function attemptRefresh(isRetry: boolean) {
      if (cancelled) return;

      const idle = Date.now() - lastActivityRef.current;
      if (idle >= INACTIVITY_LIMIT_MS) return; // genuinely idle — let the TTL lapse naturally

      try {
        const fresh = await refreshToken();
        if (cancelled) return;
        setAuthSession({
          accessToken: fresh.accessToken,
          tokenType:   fresh.tokenType,
          expiresAt:   fresh.expiresAt,
        });
        setToken(fresh.accessToken);
        setSessionExpiresAt(fresh.expiresAt ?? null);
      } catch {
        // A single transient failure (network blip, momentary server error)
        // shouldn't force out a user who is actively working — retry once
        // shortly before giving up. If it never recovers, InactivityLogout's
        // own hard JWT-expiry timer is the backstop that signs the user out,
        // rather than this refresh failing fast on its own.
        if (!isRetry) {
          window.setTimeout(() => void attemptRefresh(true), 30_000);
        }
      }
    }

    const timer = window.setTimeout(() => void attemptRefresh(false), delay);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [token, sessionExpiresAt]);

  // ── Shared helper: persist token response + load user + MFA status ─────────
  const applyTokenResponse = useCallback(async (response: AuthTokenResponse) => {
    setAuthSession({
      accessToken: response.accessToken,
      tokenType: response.tokenType,
      expiresAt: response.expiresAt,
    });
    setToken(response.accessToken);
    setSessionExpiresAt(response.expiresAt ?? null);

    // Run in parallel — MFA status is needed immediately for the enrollment gate.
    const [me, mfaStatus] = await Promise.all([
      getCurrentUser(),
      getMfaStatus().catch(() => null),
    ]);
    setCurrentUser(me);
    setMfaEnabled(mfaStatus?.enabled ?? false);
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (payload: LoginRequest): Promise<LoginResponse> => {
    const response = await loginApi(payload);

    // MFA_REQUIRED — don't set a session yet; the caller drives the MFA step.
    if (response.status === "MFA_REQUIRED") {
      return response;
    }

    await applyTokenResponse(response as AuthTokenResponse);
    return response;
  }, [applyTokenResponse]);

  // ── MFA verify ────────────────────────────────────────────────────────────
  const verifyMfa = useCallback(async (payload: MfaVerifyRequest): Promise<AuthTokenResponse> => {
    const response = await verifyMfaApi(payload);
    await applyTokenResponse(response);
    return response;
  }, [applyTokenResponse]);

  // ── Sign out ──────────────────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // ignore — still clear local session
    } finally {
      clearAuthStorage();
      setToken(null);
      setCurrentUser(null);
      setMfaEnabled(null);
      setSessionExpiresAt(null);
    }
  }, []);

  // ── Role / permission helpers ─────────────────────────────────────────────
  const hasRole = useCallback(
    (role: string) => {
      if (!currentUser?.roles?.length) return false;
      const normalized = role.trim().toUpperCase();
      return currentUser.roles.some((r) => r.trim().toUpperCase() === normalized);
    },
    [currentUser],
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => roles.some((role) => hasRole(role)),
    [hasRole],
  );

  const hasPermission = useCallback(
    (permission: string) => {
      if (!currentUser?.permissions?.length) return false;
      const normalized = permission.trim().toUpperCase();
      return currentUser.permissions.some(
        (p) => p.trim().toUpperCase() === normalized,
      );
    },
    [currentUser],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) =>
      permissions.some((permission) => hasPermission(permission)),
    [hasPermission],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated,
      isBootstrapping,
      token,
      currentUser,
      mfaEnabled,
      setMfaEnabled,
      sessionExpiresAt,
      login,
      verifyMfa,
      signOut,
      refreshCurrentUser,
      hasRole,
      hasAnyRole,
      hasPermission,
      hasAnyPermission,
    }),
    [
      isAuthenticated,
      isBootstrapping,
      token,
      currentUser,
      mfaEnabled,
      sessionExpiresAt,
      login,
      verifyMfa,
      signOut,
      refreshCurrentUser,
      hasRole,
      hasAnyRole,
      hasPermission,
      hasAnyPermission,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
